import { ref } from 'vue';

type Tone = 'success' | 'info' | 'warn' | 'error';

/** The subset of VSnackbar props a notice needs. Vuetify does not re-export its SnackbarMessage. */
interface QueuedNotice {
  title: string;
  text?: string;
  color: string;
  timeout: number;
}

export interface Notice {
  tone: Tone;
  summary: string;
  detail?: string;
  /** Auto-dismiss delay in ms. Defaults by tone (success 3s, error 60s); pass to override. */
  life?: number;
}

/** Confirmation for a destructive action. Anything reversible should just happen. */
export interface ConfirmRequest {
  title: string;
  message: string;
  acceptLabel: string;
  onAccept: () => void;
}

const toneColor: Record<Tone, string> = { success: 'success', info: 'info', warn: 'warning', error: 'error' };
const toneDefaultLife: Partial<Record<Tone, number>> = { success: 3_000, error: 60_000 };

// Vuetify's queue owns dismissal, so the notices are stored in its own message shape rather than
// the domain one. -1 is its "no auto-dismiss" timeout.
const notices = ref<QueuedNotice[]>([]);
const confirmRequest = ref<ConfirmRequest | null>(null);

/** App-wide notifications and destructive-action confirmation. Rendered by FeedbackHost. */
export function useFeedback() {
  function notify({ tone, summary, detail, life }: Notice): void {
    notices.value.push({
      title: summary,
      text: detail,
      color: toneColor[tone],
      timeout: life ?? toneDefaultLife[tone] ?? -1,
    });
  }

  function confirm(request: ConfirmRequest): void {
    confirmRequest.value = request;
  }

  return { notices, confirmRequest, notify, confirm };
}
